import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { Router, provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideNzIcons } from './icons-provider';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { provideNzDateFnsAdapter } from 'ng-zorro-antd/core/time';
import { CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LoggingInterceptorService } from './interceptors/logging.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http'; // Import the HTTP_INTERCEPTORS token
import { CustomCurrencyPipe } from './pipes/custom-currency.pipe';
import { InitThemeService } from '@services/init-theme.service';
import { ThemeSkinService } from '@services/theme-skin.service';
import * as Sentry from '@sentry/angular';
import { RequestInterceptor } from './interceptors/request.interceptor';
import { ResponseInterceptor } from './interceptors/response.interceptor';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { TimeFormatPipe } from './pipes/time-format.pipe';
import { LocalDateToUtcPipe } from './pipes/local-date-to-utc.pipe';
import { UtcToLocalDatePipe } from './pipes/utc-to-local-date.pipe';
import { provideMarkdown } from 'ngx-markdown';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { I18nService } from '@services/i18n.service';
import { environment } from '@env/environment';
import { NzModalModule } from 'ng-zorro-antd/modal';
registerLocaleData(en);

export function InitThemeServiceFactory(initThemeService: InitThemeService) {
  return async (): Promise<void> => await initThemeService.initTheme();
}

const i18nDebugSuffix = environment.i18nDebug ? '?debug=true' : '';
const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
  new TranslateHttpLoader(http, environment.i18nUrl, '.json' + i18nDebugSuffix);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideNzIcons(),
    provideNzI18n(en_US),
    provideNzDateFnsAdapter(),
    importProvidersFrom(FormsModule),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: InitThemeServiceFactory,
      deps: [InitThemeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (themeService: ThemeSkinService) => () => {
        return themeService.loadTheme();
      },
      deps: [ThemeSkinService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (i18nService: I18nService) => () => {
        return i18nService.init();
      },
      deps: [I18nService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideMarkdown({ loader: HttpClient }),
    CustomCurrencyPipe,
    CurrencyPipe,
    DatePipe,
    DateFormatPipe,
    TimeFormatPipe,
    LocalDateToUtcPipe,
    UtcToLocalDatePipe,
    importProvidersFrom([
      NzModalModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
  ],
};
